import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { ampli } from "../../ampli";
import PrimaryButton from "../../components/common/PrimaryButton";
// components
import FeedBackReviews from "../../components/userProfile/feedBackReviews";
import { MobileNumberData } from "../../components/userProfile/mobile-number";
import PaymentMethod from "../../components/userProfile/paymentMethod";
import PersonalData from "../../components/userProfile/personalData";
import { ImageUploader } from "../../components/userProfile/personalData/imageUploader";
import SecurityCode from "../../components/userProfile/securityCode";
import { EUploadImageType } from "../../s3Integration/enums";
import { uploadFile } from "../../s3Integration/s3Client";
import { updateProfileInfo } from "../../setup/api/services/profile";
import { IMyInfoData } from "../../setup/api/services/profile/models";
// apis
import { getBasicUserDetailsById } from "../../setup/api/services/user";
// types
import { IUserInfoResponseData } from "../../setup/api/services/user/models";
import { PROFILE_PATH_PARAMS } from "../../setup/constants";
import { fetchAndSetMyInfo } from "../../setup/context/api";
// providers context
import { AuthConfig } from "../../setup/context/config/authConfig";
import { EVENT_LOCAL_STORAGE_UPDATE_MY_INFO } from "../../setup/context/events";
import AbsherIdentification from "./AbsherIdentification";
import Tabbar from "./Tabbar";
// styles
import styles from "./userProfile.module.scss";

export enum SelectedTab {
  PROFILE_TAB = "PROFILE_TAB",
  MOBILE_TAB = "MOBILE_TAB",
  SECURITY_TAB = "SECURITY_TAB",
  PAYMENT_TAB = "PAYMENT_TAB",
  FEEDBACK_TAB = "FEEDBACK_TAB",
}
const UserProfile = (): JSX.Element => {
  const { t } = useTranslation();
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [userProfileInfo, setUserProfileInfo] = useState<IMyInfoData>();

  // image uploading
  const [imageUploading, setImageUploading] = useState<boolean>(false);

  const [imageUrl, setImageUrl] = useState<any>("");

  const userId = AuthConfig?.getUserInfo()?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const path = searchParams.get("path");
  useEffect(() => {
    window.addEventListener(EVENT_LOCAL_STORAGE_UPDATE_MY_INFO, () => {
      const myInfo = AuthConfig.getMyInfo();
      setUserProfileInfo(myInfo);
    });
  }, [userId]);
  useEffect(() => {
    const myInfo = AuthConfig.getMyInfo();
    setUserProfileInfo(myInfo);
  }, []);
  useEffect(() => {
    switch (path) {
      case PROFILE_PATH_PARAMS.ME:
        setSelectedTab(SelectedTab.PROFILE_TAB);
        return;
      case PROFILE_PATH_PARAMS.PAYMENT_METHOD:
        setSelectedTab(SelectedTab.PAYMENT_TAB);
        return;
      case PROFILE_PATH_PARAMS.PIN_CODE:
        setSelectedTab(SelectedTab.SECURITY_TAB);
        return;
      case PROFILE_PATH_PARAMS.MOBILE_NUMBER:
        setSelectedTab(SelectedTab.MOBILE_TAB);
        return;
      case PROFILE_PATH_PARAMS.FEEDBACK:
        setSelectedTab(SelectedTab.FEEDBACK_TAB);
        return;
      default:
        return setSelectedTab(SelectedTab.PROFILE_TAB);
    }
  }, [path]);
  const [selectedTab, setSelectedTab] = useState<SelectedTab>(
    SelectedTab.PROFILE_TAB
  );
  const [userDetails, setUserDetials] = useState<
    IUserInfoResponseData | undefined
  >(undefined);

  const fetchUserDetails = useCallback(async (userId: number) => {
    if (userId) {
      try {
        const response = await getBasicUserDetailsById(Number(userId));
        setUserDetials(response.data);
      } catch (error) {
        Sentry.captureException(error);
        console.error("error", error);
      }
    }
  }, []);

  useEffect(() => {
    if (userId) fetchUserDetails(Number(userId));
  }, [fetchUserDetails, userId]);

  const PageDisplay = () => {
    switch (selectedTab) {
      case SelectedTab.PROFILE_TAB:
        return (
          <PersonalData
            profileFirstName={userDetails?.profile?.firstName || ""}
            profileLastName={userDetails?.profile?.lastName || ""}
            photoUrl={imageUrl || userDetails?.profile?.photoUrl}
            updateData={() => fetchUserDetails(Number(userId))}
          />
        );
      case SelectedTab.MOBILE_TAB:
        return <MobileNumberData />;
      case SelectedTab.SECURITY_TAB:
        return <SecurityCode />;
      case SelectedTab.PAYMENT_TAB:
        return <PaymentMethod />;
      case SelectedTab.FEEDBACK_TAB:
        return <FeedBackReviews />;
      default:
        return (
          <PersonalData
            profileFirstName={userDetails?.profile?.firstName || ""}
            profileLastName={userDetails?.profile?.lastName || ""}
            photoUrl={userDetails?.profile?.photoUrl}
            updateData={() => fetchUserDetails(Number(userId))}
          />
        );
    }
  };

  const handleSelection = useCallback(
    (selected: SelectedTab) => {
      setSelectedTab(selected);
      switch (selected) {
        case SelectedTab.FEEDBACK_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.FEEDBACK });
          return;
        case SelectedTab.PROFILE_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.ME });
          return;
        case SelectedTab.MOBILE_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.MOBILE_NUMBER });
          return;
        case SelectedTab.SECURITY_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.PIN_CODE });
          return;
        case SelectedTab.PAYMENT_TAB:
          setSearchParams({ path: PROFILE_PATH_PARAMS.PAYMENT_METHOD });
          return;
        default:
          return;
      }
    },
    [setSearchParams]
  );

  const sendAmpliUserProperties = useCallback((data: IMyInfoData) => {
    if (data.id) {
      ampli.client.setUserId(data.id.toString());
      ampli.client.setUserProperties({
        userId: data.id,
        userName: data.profile.fullName || "",
      });
    }
  }, []);

  const handleImageUpload = useCallback(
    async (e: any) => {
      setImageUploading(true);
      const image = e?.target?.files[0];
      try {
        const url = await uploadFile(image, EUploadImageType.PROFILE, userId);
        setImageUrl(url);
        const data = {
          firstName: userDetails?.profile?.firstName || "",
          lastName: userDetails?.profile?.lastName || "",
          photoUrl: url,
        };
        try {
          if (userId) {
            const res = await updateProfileInfo(userId, data);
            sendAmpliUserProperties(res.data);
            await fetchAndSetMyInfo();
          }
        } catch (error) {
          Sentry.captureException(error);
        }
        setImageUploading(false);
      } catch (error) {
        Sentry.captureException(error);
        console.error(e);
        setImageUploading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userDetails?.profile?.firstName, userDetails?.profile?.lastName, userId]
  );

  useEffect(() => {
    if (linkCopied) {
      setTimeout(() => {
        setLinkCopied(false);
      }, 3000);
    }
  }, [linkCopied]);

  const sendShareProfileLinkEvent = useCallback(() => {
    try {
      const properties = {
        shareProfileDate: JSON.stringify(new Date()),
      };
      ampli && ampli.shareProfileLink(properties);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, []);

  const onCopyClick = useCallback(async () => {
    try {
      await navigator?.clipboard?.writeText(
        `${process.env.REACT_APP_URL}user/${userId}`
      );
      setLinkCopied(true);
      sendShareProfileLinkEvent();
    } catch (error) {
      console.error({ error });
    }
  }, [sendShareProfileLinkEvent, userId]);

  const closeLinkCopied = useCallback(() => {
    setLinkCopied(false);
  }, []);

  const renderTop = useCallback(
    () => (
      <div className={styles.top_section}>
        <div className={styles.profile_section}>
          <div className={styles.image_uploader}>
            <ImageUploader
              imageUploading={imageUploading}
              imageUrl={imageUrl || userDetails?.profile?.photoUrl}
              handleOnImageChange={handleImageUpload}
              showVerification={true}
              nationalIdVerified={userDetails?.nationalIdVerified}
            />
          </div>
          <div className={styles.user_details}>
            <h2>{userDetails?.profile?.fullName || ""}</h2>
            <p dir="ltr" className={classNames(styles.p2, styles.g700)}>
              {userDetails?.phoneNumber || ""}
            </p>
          </div>
        </div>
        <div className={styles.call_to_actions}>
          {linkCopied && (
            <div className={styles.alert_section}>
              <img alt="close" src={"/assets/images/svg/alertCorrect.svg"} />
              <p className={classNames(styles.p2, styles.white)}>
                {t("lbl_profile_link_copied")}
              </p>
              <img
                onClick={closeLinkCopied}
                className={styles.pointer}
                alt="close"
                src={"/assets/images/svg/close.svg"}
              />
            </div>
          )}
          <div className={styles.share_section}>
            <PrimaryButton backgroundColor={"#fff"} onClick={onCopyClick}>
              <div className={styles.profile_sharing}>
                <img alt="close" src={"/assets/images/svg/share.svg"} />
                <p className={styles.p2}>{t("lbl_btn_profile_sharing")}</p>
              </div>
            </PrimaryButton>
          </div>
          <div className={styles.absher_section}>
            {!userDetails?.nationalIdVerified && (
              <AbsherIdentification
                handleRefresh={() => fetchUserDetails(Number(userId))}
              />
            )}
          </div>
        </div>
      </div>
    ),
    [
      fetchUserDetails,
      closeLinkCopied,
      handleImageUpload,
      imageUploading,
      imageUrl,
      linkCopied,
      onCopyClick,
      t,
      userDetails?.nationalIdVerified,
      userDetails?.phoneNumber,
      userDetails?.profile?.fullName,
      userDetails?.profile?.photoUrl,
      userId,
    ]
  );

  const renderTabbar = useCallback(
    () => (
      <Tabbar selectedTab={selectedTab} handleSelection={handleSelection} />
    ),
    [handleSelection, selectedTab]
  );

  return (
    <div className={styles.profile_container}>
      {renderTop()}
      {renderTabbar()}
      {PageDisplay()}
    </div>
  );
};

export default UserProfile;
